import React, { useEffect, useState } from 'react'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom'
import { AppHeader } from './components/app-header/AppHeader'
import { SignInForm } from './components/content/account/SignInForm'
import { NotFoundPage } from './components/content/NotFoundPage'
import { SidebarMenu } from './components/navigation-sidebar/SidebarMenu'
import { ProtectedRoute } from './components/ProtectedRoute'
import { UPDATE_CLAIMS } from './constants'
import { signOutFromFirebase } from './firebase/firebaseSetup'
import { RequestMethod } from './model/enums/enums'
import { MokaiUser } from './model/mokaiUser'
import config from './mokai-firebase-config.json'
import {
  AllRoutes,
  AuthRoutes,
  MokaiRoute,
  PublicRoutes,
} from './router/routes'
import { handleRequest } from './services/api/base/handleRequest'
import { loadBasicInformation } from './services/api/basicApiService'
import { sendWarmup } from './services/api/mokaiApiService'
import { getJWTObject } from './util/jwtDecode'

import 'firebase/compat/auth'
import 'firebase/compat/firestore'
import { initializeApp } from 'firebase/app'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { openDokai } from './services/help/openDokai'
import { SetupDateLocale } from './util/setupDateLocale'
import { SetupLanguage } from './util/setupLanguage'
import { hasAnyRequiredClaim } from './util/validation/hasClaim'
import { t } from 'i18next'
import { useAppDispatch, useAppSelector } from './redux/hooks'
import { removedUser, userSet } from './redux/basic/user/userSlice'
import { claimsRemoved, claimsSet } from './redux/basic/user/claimSlice'

const app = initializeApp(config.firebaseConfig)

export const auth = getAuth()
auth.useDeviceLanguage()

export const App = () => {
  const [hasSetClaims, setHasSetClaims] = useState<boolean>(false)
  const [authChangeOngoing, setAuthStateOngoing] = useState<boolean>(false)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const user = useAppSelector((state) => state.user)
  const userClaims = useAppSelector((state) => state.claims)

  const dispatcher = useAppDispatch()

  const loadAllBasicInformation = async (claims: string[]) => {
    if (isLoading || user.loadedBasicInformation || !claims) {
      return
    }
    setIsLoading(true)
    const loadingResult = await loadBasicInformation(claims, dispatcher).catch(
      (err: any) => console.error(err)
    )
    if (loadingResult) {
      setIsLoading(false)
      console.log('[App] Successfully loaded all basic information.')
    } else {
      setIsLoading(false)
      console.warn('[App] Could not load all basic information successful.')
    }
  }

  const navigate = useNavigate()
  const location = useLocation()

  const signOutUser = async () => {
    dispatcher(removedUser())
    dispatcher(claimsRemoved())
    await signOutFromFirebase()
    localStorage.removeItem('mokai-user')
    localStorage.clearToken()
    window.location.reload()
  }

  const handleKeyDown = (event: any) => {
    if (event.shiftKey && event.code === 'F1') {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      openDokai(location)
    }
    if (event.ctrlKey && event.key === 's') {
      event.preventDefault()
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  const updateClaims = async () => {
    await handleRequest(UPDATE_CLAIMS, RequestMethod.GET).catch(
      (error: any) => {
        console.error(error)
        signOutUser()
        console.log('[CLAIMS] Failed to update claims - signing out.')
        return
      }
    )
    const token = await auth.currentUser?.getIdToken(true)
    if (token) {
      const decodedJWT = getJWTObject(token)
      if (!decodedJWT?.modules) {
        console.warn(
          '[CLAIMS] Something went wrong updating the claims and renewing the token'
        )
        signOutUser()
        return
      }
      const claims = decodedJWT.modules
      dispatcher(claimsSet(claims))
      console.info('[CLAIMS] Successfully updated claims and renewed the token')
      loadAllBasicInformation(claims)
    }
  }

  const handleAuthStateChange = async (firebaseUser: any) => {
    setAuthStateOngoing(true)

    if (!firebaseUser.accessToken) {
      console.warn('Could not get token, singing out user')
      await signOutUser()
      return
    }

    if (firebaseUser.accessToken === user.token) {
      return
    }

    const decodedJWT = getJWTObject(firebaseUser.accessToken)
    //console.log(JSON.stringify(decodedJWT))

    const mokaiUser: MokaiUser = {
      userId: firebaseUser.uid,
      token: firebaseUser.accessToken,
      personId: decodedJWT.id_person,
      email: decodedJWT.email,
      displayName: decodedJWT.name,
      apiKey: firebaseUser?.providerId,
      firebaseUser: auth?.currentUser?.toString(),
      loadedBasicInformation: user.loadedBasicInformation,
    }

    localStorage.setItem('mokai-user', '1')

    dispatcher(userSet(mokaiUser))

    updateClaims().then(() => {
      setHasSetClaims(true)
    })

    // if coming from sign-in, switch to dashboard else stay on page
    if (location.pathname === PublicRoutes.login || location.pathname === '/') {
      navigate(AuthRoutes.dashboard)
    }

    updateClaims().then(() => {
      setHasSetClaims(true)
    })
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (authChangeOngoing || !firebaseUser) {
        return
      }
      setAuthStateOngoing(true)

      handleAuthStateChange(firebaseUser).finally(() =>
        setAuthStateOngoing(false)
      )
    })
    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    sendWarmup()

    SetupLanguage()

    // set dayjs locales globally
    SetupDateLocale()

    // send the warmup every minute to keep the cloud run services running,
    // while someone has this app open. on chrome throttling will activate after 5min
    const interval = setInterval(sendWarmup, 240000) // call warmup every 4 minutes (240'000ms)

    return () => clearInterval(interval)
  }, [])

  return (
    <div className={'h-full w-full'}>
      {localStorage.getItem('mokai-user') !== '1' &&
        location.pathname !== PublicRoutes.login && (
          <Navigate to={PublicRoutes.login} />
        )}
      <>
        <AppHeader />
        <div className={'flex content-height flex-grow-1 w-full'}>
          <SidebarMenu />
          <Routes>
            <Route index path={PublicRoutes.login} element={<SignInForm />} />
            {hasSetClaims &&
              AllRoutes.map((route: MokaiRoute, key: number) => {
                let requiredRoutes: string[] = [route.mainModule]
                if (route.subModules) {
                  requiredRoutes = requiredRoutes.concat(route?.subModules)
                }
                const userHasClaim = hasAnyRequiredClaim(
                  userClaims,
                  requiredRoutes
                )
                if (userHasClaim) {
                  return (
                    <Route
                      key={key}
                      caseSensitive={route.exact}
                      path={route.path}
                      element={
                        <div
                          className={'flex content-height flex-grow-1 w-full'}
                        >
                          <div className={'card pl-2 w-full overflow-auto'}>
                            {/* For more diverse functionality in terms of routing
										(title depending on content)
										We can add an empty title that enables us to add the title
										in the component itself */}
                            {route.name !== 'empty' && (
                              <h2 className={'m-0  pt-2 pl-0'}>
                                {t('title.' + route?.name)}
                              </h2>
                            )}
                            {route.component}
                          </div>
                        </div>
                      }
                    />
                  )
                }
              })}
            <Route
              path={'*'}
              element={
                <ProtectedRoute>
                  <NotFoundPage />
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      </>
    </div>
  )
}